import type { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface Props {
  titel: string;
  icon: LucideIcon;
  /** Rechts oben, führt auf die vollständige Liste. */
  mehr?: { label: string; onClick: () => void };
  /** Ein zweiter Weg links neben `mehr`, etwa wenn die Vollansicht und die Post auseinanderliegen. */
  daneben?: { label: string; onClick: () => void };
  /** Steht statt der Liste da, wenn nichts anliegt. */
  leer: string;
  /**
   * Wie viele Einträge es insgesamt gibt. Ohne Angabe entfällt der Hinweis
   * „und n weitere“ — etwa dort, wo schon die Abfrage begrenzt war und die
   * Gesamtzahl gar nicht bekannt ist.
   */
  gesamt?: number;
  /** Wie viele Zeilen `children` enthält. Entscheidet über den leeren Zustand. */
  gezeigt: number;
  children: React.ReactNode;
}

/**
 * Der gemeinsame Rahmen der vier Arbeitslisten.
 *
 * Kopfzeile, leerer Zustand und der Hinweis „und n weitere“ sehen überall
 * gleich aus — sie stehen deshalb einmal hier und nicht viermal daneben.
 */
export function ListenKarte({
  titel,
  icon: Icon,
  mehr,
  daneben,
  leer,
  gesamt,
  gezeigt,
  children,
}: Props) {
  const weitere = gesamt === undefined ? 0 : gesamt - gezeigt;

  return (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {titel}
        </h3>
        <div className="flex items-center gap-3">
          {[daneben, mehr].map(
            (link) =>
              link && (
                <button
                  key={link.label}
                  onClick={link.onClick}
                  className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {link.label}
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              ),
          )}
        </div>
      </div>

      {gezeigt === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">{leer}</p>
      ) : (
        <>
          <ul className="divide-y divide-border">{children}</ul>
          {weitere > 0 && mehr && (
            <button
              onClick={mehr.onClick}
              className="border-t border-border px-4 py-2 text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              und {weitere} weitere
            </button>
          )}
        </>
      )}
    </Card>
  );
}
